import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { billingAPI, assistantAPI } from '../../api/client';

export default function HelpAssistant() {
  const location = useLocation();
  const [planSlug, setPlanSlug] = useState(null);
  const isProOrAgency = planSlug === 'pro' || planSlug === 'agency';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachedImage, setAttachedImage] = useState(null); // {previewUrl, base64}
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    billingAPI.individualPlan().then(r => setPlanSlug(r.data?.slug || null)).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  if (!isProOrAgency) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Solo se pueden adjuntar imágenes.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result; // data:image/xxx;base64,....
      const base64 = result.split(',')[1];
      setAttachedImage({ previewUrl: result, base64 });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const send = async () => {
    const text = input.trim();
    if ((!text && !attachedImage) || loading) return;
    setError('');
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    const displayText = text || '📎 (captura adjunta)';
    setMessages(m => [...m, { role: 'user', content: displayText, imagePreview: attachedImage?.previewUrl }]);
    const imageToSend = attachedImage?.base64;
    setInput('');
    setAttachedImage(null);
    setLoading(true);
    try {
      const { data } = await assistantAPI.chat(text, history, location.pathname, imageToSend);
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo enviar el mensaje.');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {open && (
        <div style={{
          position: 'fixed', bottom: '150px', right: '20px', width: '340px', maxWidth: 'calc(100vw - 32px)',
          height: '460px', maxHeight: 'calc(100vh - 140px)', background: 'var(--bg-card)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-lg)',
          display: 'flex', flexDirection: 'column', zIndex: 500, overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/thitan-logo.png" alt="TX-Flow AI" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: '700' }}>Asistente TX-Flow AI</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Pregúntame cómo usar la plataforma</div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px' }}>✕</button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.length === 0 && (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', marginTop: '20px' }}>
                👋 Hola, soy tu asistente. Pregúntame sobre cualquier módulo (CRM, Ads, Reservas, Publicar y más) o cuéntame el problema técnico que tienes — puedes adjuntar una captura del error y te ayudo a diagnosticarlo, o lo reporto a soporte.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                background: m.role === 'user' ? 'var(--gradient-primary)' : 'var(--bg-elevated)',
                color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                padding: '9px 13px', borderRadius: '14px', fontSize: '13px', lineHeight: 1.5, maxWidth: '85%',
                whiteSpace: 'pre-wrap',
              }}>
                {m.imagePreview && <img src={m.imagePreview} alt="captura adjunta" style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '6px', display: 'block' }} />}
                {m.content}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', color: 'var(--text-muted)', fontSize: '13px' }}>Escribiendo...</div>
            )}
            {error && (
              <div style={{ color: '#ef4444', fontSize: '12px', textAlign: 'center' }}>{error}</div>
            )}
            <div ref={bottomRef} />
          </div>

          <div style={{ borderTop: '1px solid var(--border)' }}>
            {attachedImage && (
              <div style={{ padding: '8px 10px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={attachedImage.previewUrl} alt="preview" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)' }} />
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', flex: 1 }}>Captura lista para enviar</span>
                <button onClick={() => setAttachedImage(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px' }}>✕</button>
              </div>
            )}
            <div style={{ padding: '10px', display: 'flex', gap: '8px' }}>
              <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />
              <button
                onClick={() => fileInputRef.current?.click()}
                title="Adjuntar captura de un error"
                style={{
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-secondary)',
                  borderRadius: '10px', width: '38px', cursor: 'pointer', fontSize: '15px', flexShrink: 0,
                }}
              >📎</button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Escribe tu pregunta o describe un error..."
                rows={1}
                style={{
                  flex: 1, resize: 'none', background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  borderRadius: '10px', padding: '9px 12px', color: 'var(--text-primary)', fontSize: '13px',
                  fontFamily: 'var(--font-body)', outline: 'none',
                }}
              />
              <button
                onClick={send}
                disabled={loading || (!input.trim() && !attachedImage)}
                style={{
                  background: 'var(--gradient-primary)', border: 'none', color: '#fff', borderRadius: '10px',
                  padding: '0 16px', cursor: loading ? 'default' : 'pointer', fontSize: '13px', fontWeight: '700',
                  opacity: loading || (!input.trim() && !attachedImage) ? 0.6 : 1,
                }}
              >➤</button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        title="Asistente de ayuda"
        style={{
          position: 'fixed', bottom: '110px', right: '20px', width: '68px', height: '68px', borderRadius: '50%',
          background: 'var(--bg-card)', border: '2px solid transparent',
          backgroundImage: 'linear-gradient(var(--bg-card), var(--bg-card)), var(--gradient-primary)',
          backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box',
          boxShadow: 'var(--shadow-glow)', cursor: 'pointer', zIndex: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, overflow: 'visible',
        }}
      >
        {open ? (
          <span style={{ fontSize: '22px', color: 'var(--text-primary)' }}>✕</span>
        ) : (
          <>
            <img
              src="/thitan-logo.png"
              alt="Asistente TX-Flow AI"
              className="animate-float"
              style={{ width: '44px', height: '44px', objectFit: 'contain' }}
            />
            <span style={{
              position: 'absolute', top: '-4px', right: '-4px', width: '22px', height: '22px',
              borderRadius: '50%', background: 'var(--gradient-primary)', color: '#fff',
              fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid var(--bg-base)', fontWeight: '800',
            }}>💬</span>
          </>
        )}
      </button>
    </>
  );
}
